import { useCallback, useEffect, useState } from "react";
import { webinarsService } from "../features/webinars/service";
import { newsService } from "../features/news/service";
import type { WebinarCardResponse } from "../features/webinars/types";
import type { NewsArticle } from "../features/news/service";
import { formatIST } from "../utils/dateUtils";

type Tab = "upcoming" | "completed" | "news";

const TABS: { key: Tab; label: string }[] = [
  { key: "upcoming", label: "Upcoming" },
  { key: "completed", label: "Completed" },
  { key: "news", label: "News" },
];

export default function WebinarsNewsPage() {
  const [tab, setTab] = useState<Tab>("upcoming");
  const [webinars, setWebinars] = useState<WebinarCardResponse[]>([]);
  const [news, setNews] = useState<NewsArticle[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (activeTab: Tab) => {
    setLoading(true);
    try {
      if (activeTab === "news") {
        const res = await newsService.listNews(0, 40);
        setNews(res.items || []);
      } else {
        const res = await webinarsService.listWebinarCards(0, 40, undefined, activeTab);
        setWebinars(res.items || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(tab); }, [tab, load]);

  return (
    <div className="page-shell max-w-5xl mx-auto p-6">
      <h1 className="text-2xl font-bold text-white mb-4">Webinars & News</h1>

      <div className="flex gap-2 mb-6 border-b border-gray-800">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === t.key ? "border-indigo-500 text-white" : "border-transparent text-gray-500 hover:text-gray-300"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-gray-500 text-center py-20">Loading...</p>
      ) : tab === "news" ? (
        news.length === 0 ? (
          <p className="text-gray-500 text-center py-20">No news articles yet.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {news.map((article) => (
              <div key={article.id} className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
                {article.cover_image_url && (
                  <img src={article.cover_image_url} alt={article.title} className="w-full h-40 object-cover" />
                )}
                <div className="p-4">
                  <h3 className="text-white font-medium mb-1">{article.title}</h3>
                  {article.summary && <p className="text-gray-400 text-sm mb-2 line-clamp-3">{article.summary}</p>}
                  {article.source_url && (
                    <a href={article.source_url} target="_blank" rel="noreferrer" className="text-indigo-400 text-sm hover:underline">
                      {article.source || "Read more"}
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )
      ) : webinars.length === 0 ? (
        <p className="text-gray-500 text-center py-20">
          {tab === "upcoming" ? "No upcoming webinars." : "No completed webinars yet."}
        </p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {webinars.map((w) => (
            <div key={w.id} className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
              {tab === "completed" && w.video_url ? (
                <video src={w.video_url} controls className="w-full h-40 object-cover bg-black" />
              ) : w.thumbnail_url ? (
                <img src={w.thumbnail_url} alt={w.title} className="w-full h-40 object-cover" />
              ) : null}
              <div className="p-4">
                <h3 className="text-white font-medium mb-1">{w.title}</h3>
                {w.description && <p className="text-gray-400 text-sm mb-2 line-clamp-3">{w.description}</p>}
                {w.presenter && <p className="text-gray-500 text-xs mb-1">Presenter: {w.presenter}</p>}
                {w.webinar_date && <p className="text-gray-500 text-xs mb-2">{formatIST(w.webinar_date)}</p>}
                {tab === "upcoming" && w.webinar_link && (
                  <a href={w.webinar_link} target="_blank" rel="noreferrer" className="text-indigo-400 text-sm hover:underline">
                    Join webinar
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
